import { Component } from '@theme/component';

export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Array<() => void>} */
  #cleanups = [];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    // Inject src attributes (required for JS-loaded sources)
    for (const source of videoSources) {
      const { videoSource } = source.dataset;
      if (videoSource) source.setAttribute('src', videoSource);
    }

    // Force attributes for all browsers
    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.playsInline = true;
    videoElement.autoplay = true;
    videoElement.setAttribute('playsinline', '');
    videoElement.setAttribute('webkit-playsinline', '');
    videoElement.setAttribute('muted', '');
    videoElement.setAttribute('autoplay', '');

    // Lecture en boucle : l'attribut HTML seul ne suffit pas sur mobile, où
    // certains navigateurs (iOS surtout) le perdent après un `load()` ou après
    // une mise en pause. On le repose côté propriété ET côté attribut, et on
    // ajoute un filet de sécurité sur l'événement `ended` plus bas.
    videoElement.loop = true;
    videoElement.setAttribute('loop', '');

    videoElement.load();

    let visible = true;

    const tryPlay = () => {
      // Ne pas relancer une vidéo hors champ : l'observer l'a mise en pause
      // volontairement pour économiser la batterie.
      if (!visible) return;

      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Silently handle autoplay block
        });
      }
    };

    const playWhenReady = () => {
      if (videoElement.readyState >= 2) {
        tryPlay();
      } else {
        videoElement.addEventListener('canplay', tryPlay, { once: true });
        this.#cleanups.push(() => videoElement.removeEventListener('canplay', tryPlay));
      }
    };

    /**
     * @param {EventTarget} target
     * @param {string} type
     * @param {EventListener} handler
     * @param {AddEventListenerOptions} [options]
     */
    const listen = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      this.#cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    // Filet de sécurité de la boucle : si la vidéo s'arrête malgré `loop`
    // (comportement observé sur iOS et sur certains Android), on la rembobine
    // et on la relance nous-mêmes. Invisible pour le visiteur.
    listen(videoElement, 'ended', () => {
      videoElement.currentTime = 0;
      tryPlay();
    });

    // Même filet, côté pause : une pause que nous n'avons pas demandée (appel
    // entrant, changement d'application, économiseur d'énergie) est annulée dès
    // que c'est possible.
    listen(videoElement, 'pause', () => {
      if (!visible || videoElement.ended) return;
      requestAnimationFrame(tryPlay);
    });

    // Retour sur l'onglet ou sur la page (bouton Précédent, changement
    // d'application sur mobile) : la lecture reprend.
    listen(document, 'visibilitychange', () => {
      if (!document.hidden) tryPlay();
    });
    listen(window, 'pageshow', tryPlay);

    // Dernier recours : quand l'autoplay est refusé (mode économie d'énergie
    // iOS, réglages restrictifs), la première interaction du visiteur suffit à
    // débloquer la lecture. On n'écoute qu'une fois.
    const unlock = () => {
      tryPlay();
    };
    listen(document, 'touchstart', unlock, { once: true, passive: true });
    listen(document, 'click', unlock, { once: true });

    // Use IntersectionObserver to trigger play when visible
    if ('IntersectionObserver' in window) {
      this.#observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (entry.isIntersecting) {
              playWhenReady();
            } else {
              videoElement.pause();
            }
          });
        },
        { threshold: 0.1 }
      );
      this.#observer.observe(videoElement);
    } else {
      // Fallback for older browsers
      playWhenReady();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.#observer?.disconnect();
    this.#observer = null;

    for (const cleanup of this.#cleanups) cleanup();
    this.#cleanups = [];
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}